/**
 * 脚本加载工具函数
 * 用于动态加载第三方脚本，支持 requestIdleCallback 延迟加载
 * 提供了防重复加载、错误处理、批量加载等功能
 */

// 记录已加载或正在加载的脚本，防止重复加载
const scriptLoadingStatus = new Map();

// 脚本加载状态枚举
const SCRIPT_STATUS = {
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
};

/**
 * requestIdleCallback 的 Safari 兼容性处理
 *
 * 背景：
 * - requestIdleCallback 允许在浏览器空闲时执行低优先级任务
 * - Safari 浏览器不支持此 API，需要降级处理
 *
 * 实现方式：
 * - 优先使用原生 requestIdleCallback
 * - Safari 降级使用 setTimeout 模拟
 *
 * @param {Function} callback - 空闲时执行的回调函数
 * @param {Object} options - 配置选项
 * @param {number} [options.timeout=1] - 超时时间（毫秒），超时后强制执行
 * @returns {number|undefined} 返回 ID，可用于取消执行
 *
 * @example
 * const id = requestIdleCallbackWithFallback(() => {
 *   console.log('浏览器空闲时执行');
 * }, { timeout: 2000 });
 */
export function requestIdleCallbackWithFallback(callback, options) {
  // 服务端渲染环境检查
  if (typeof window === 'undefined') {
    return;
  }

  // 优先使用原生 API
  if (window.requestIdleCallback) {
    // 如果没有传递 options 或者 options 为空对象，则不传第二个参数
    // 避免传递空对象导致 IdleRequestOptions 类型错误
    if (options && Object.keys(options).length > 0) {
      return window.requestIdleCallback(callback, options);
    }
    return window.requestIdleCallback(callback);
  }

  // Safari 降级处理：使用 setTimeout 模拟
  // timeout 默认为 1ms，确保快速响应
  const timeout = options?.timeout || 1;
  return setTimeout(() => {
    // 模拟 requestIdleCallback 的回调参数
    callback({
      didTimeout: false, // 标记是否超时
      timeRemaining: () => 5, // 模拟剩余空闲时间（毫秒）
    });
  }, timeout);
}

/**
 * 取消 requestIdleCallback 执行
 *
 * 说明：
 * - 配合 requestIdleCallbackWithFallback 使用
 * - 自动处理原生 API 和 setTimeout 降级的差异
 *
 * @param {number} id - requestIdleCallback 或 setTimeout 返回的 ID
 *
 * @example
 * const id = requestIdleCallbackWithFallback(() => {});
 * cancelIdleCallback(id); // 取消执行
 */
export function cancelIdleCallback(id) {
  // 服务端渲染环境检查
  if (typeof window === 'undefined') {
    return;
  }

  // 使用对应的取消方法
  if (window.cancelIdleCallback) {
    window.cancelIdleCallback(id);
  } else {
    // Safari 使用 clearTimeout
    clearTimeout(id);
  }
}

/**
 * 动态加载外部脚本
 *
 * 功能：
 * - 动态创建 script 标签并加载外部脚本
 * - 自动防止重复加载同一 URL
 * - 支持 Promise 异步操作
 * - 支持加载成功/失败回调
 *
 * 防重复加载机制：
 * - 检查 DOM 中是否已存在相同 src 的 script 标签
 * - 检查内部加载状态缓存（scriptLoadingStatus）
 * - 如果正在加载，返回同一个 Promise
 *
 * @param {string} src - 脚本 URL 地址
 * @param {Object} options - 配置选项
 * @param {boolean} [options.async=true] - 是否异步加载（默认 true）
 * @param {boolean} [options.defer=false] - 是否延迟加载
 * @param {boolean} [options.insertBefore=false] - 是否插入到第一个 script 之前
 * @param {Function} [options.onLoad] - 加载成功回调
 * @param {Function} [options.onError] - 加载失败回调
 * @returns {Promise<void>} Promise 对象
 *
 * @example
 * // 基本用法
 * await loadScript('https://example.com/script.js');
 *
 * @example
 * // 带回调
 * loadScript('https://example.com/script.js', {
 *   async: true,
 *   defer: true,
 *   onLoad: () => console.log('加载成功'),
 *   onError: (err) => console.error('加载失败', err)
 * });
 */
export function loadScript(src, options = {}) {
  // 检查是否已在加载状态缓存中
  if (scriptLoadingStatus.has(src)) {
    const status = scriptLoadingStatus.get(src);

    // 如果已加载成功，直接 resolve
    if (status.state === SCRIPT_STATUS.LOADED) {
      if (options.onLoad) {
        options.onLoad();
      }
      return Promise.resolve();
    }

    // 如果正在加载，返回相同的 Promise
    if (status.state === SCRIPT_STATUS.LOADING) {
      return status.promise;
    }

    // 如果之前加载失败，清除缓存，重新尝试
    if (status.state === SCRIPT_STATUS.FAILED) {
      scriptLoadingStatus.delete(src);
    }
  }

  // 检查 DOM 中是否已存在该脚本（向后兼容）
  const existingScript = document.querySelector(`script[src="${src}"]`);
  if (existingScript) {
    if (options.onLoad) {
      options.onLoad();
    }
    // 标记为已加载
    scriptLoadingStatus.set(src, { state: SCRIPT_STATUS.LOADED });
    return Promise.resolve();
  }

  // 创建新的加载 Promise
  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;

    // 默认异步加载
    script.async = options.async !== false;

    // 可选延迟加载
    if (options.defer) {
      script.defer = true;
    }

    // 加载成功处理
    script.onload = () => {
      // 更新加载状态为成功
      scriptLoadingStatus.set(src, { state: SCRIPT_STATUS.LOADED });

      if (options.onLoad) {
        options.onLoad();
      }
      resolve();
    };

    // 加载失败处理
    script.onerror = () => {
      const error = new Error(`Failed to load script: ${src}`);

      // 更新加载状态为失败
      scriptLoadingStatus.set(src, { state: SCRIPT_STATUS.FAILED });

      if (options.onError) {
        options.onError(error);
      }
      reject(error);
    };

    // 插入到文档中
    // 可选择插入位置：第一个 script 之前 或 body 末尾
    if (options.insertBefore) {
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.body.appendChild(script);
      }
    } else {
      document.body.appendChild(script);
    }
  });

  // 缓存加载中的 Promise
  scriptLoadingStatus.set(src, {
    state: SCRIPT_STATUS.LOADING,
    promise,
  });

  return promise;
}

/**
 * 在浏览器空闲时批量加载脚本
 *
 * 场景：
 * - 页面初始化后加载非关键脚本（如分析、广告等）
 * - 避免阻塞主线程，提升用户体验
 *
 * 策略：
 * - 使用 requestIdleCallback 在浏览器空闲时加载
 * - 脚本串行加载，每个间隔 100ms，避免网络拥塞
 * - 单个脚本加载失败不影响其他脚本
 *
 * @param {Array<Object>} scripts - 脚本配置数组
 * @param {string} scripts[].src - 脚本 URL
 * @param {Object} [scripts[].options] - loadScript 的配置选项
 * @param {Object} [idleOptions] - requestIdleCallback 配置
 * @param {number} [idleOptions.timeout=2000] - 超时时间（毫秒）
 *
 * @example
 * loadScriptsInIdle([
 *   { src: 'https://example.com/analytics.js' },
 *   { src: 'https://example.com/ads.js', options: { async: true } }
 * ], { timeout: 3000 });
 */
export function loadScriptsInIdle(scripts, idleOptions = {}) {
  // 参数校验
  if (!scripts || scripts.length === 0) {
    return;
  }

  // 在浏览器空闲时执行
  requestIdleCallbackWithFallback(
    () => {
      // 串行加载脚本，避免同时发起过多请求
      // 每个脚本间隔 100ms，给浏览器缓冲时间
      scripts.forEach((scriptConfig, index) => {
        setTimeout(() => {
          loadScript(scriptConfig.src, scriptConfig.options || {}).catch((error) => {
            // 单个脚本失败不影响其他脚本，仅警告
            console.warn(`[Script Loader] 脚本加载失败: ${scriptConfig.src}`, error);
          });
        }, index * 100); // 每个脚本延迟 100ms
      });
    },
    { timeout: idleOptions.timeout || 2000 }, // 默认 2 秒超时
  );
}

/**
 * 加载内联脚本（直接执行 JavaScript 代码）
 *
 * 说明：
 * - 创建 script 标签并设置 textContent
 * - 内联脚本会立即同步执行，不需要等待 onload
 * - 适用于需要动态生成并执行的脚本代码
 *
 * 注意：
 * - 内联脚本的 onload 事件不会触发（浏览器特性）
 * - 脚本插入 DOM 后会立即执行
 * - 执行失败会触发 onerror 或 try-catch
 *
 * @param {string} code - JavaScript 代码字符串
 * @param {Object} [options] - 配置选项
 * @param {Function} [options.onLoad] - 执行成功回调（立即调用）
 * @param {Function} [options.onError] - 执行失败回调
 * @returns {Promise<void>} Promise 对象
 *
 * @example
 * loadInlineScript('console.log("Hello World");', {
 *   onLoad: () => console.log('脚本已执行'),
 *   onError: (err) => console.error('执行失败', err)
 * });
 */
export function loadInlineScript(code, options = {}) {
  return new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.textContent = code;

      // 内联脚本的 onerror 可能不会触发
      // 但仍然添加以防某些浏览器支持
      script.onerror = () => {
        const error = new Error('Failed to execute inline script');
        if (options.onError) {
          options.onError(error);
        }
        reject(error);
      };

      // 插入到 DOM，脚本会立即执行
      document.body.appendChild(script);

      // 内联脚本立即执行完成，不需要等待异步加载
      // 调用成功回调并 resolve
      if (options.onLoad) {
        options.onLoad();
      }
      resolve();
    } catch (error) {
      // 捕获同步执行错误
      if (options.onError) {
        options.onError(error);
      }
      reject(error);
    }
  });
}

/**
 * 清除脚本加载缓存
 *
 * 场景：
 * - 需要强制重新加载某个脚本
 * - 测试环境需要清空缓存
 *
 * @param {string} [src] - 可选，指定清除某个 URL 的缓存；不传则清空所有缓存
 *
 * @example
 * // 清除特定脚本缓存
 * clearScriptCache('https://example.com/script.js');
 *
 * @example
 * // 清空所有缓存
 * clearScriptCache();
 */
export function clearScriptCache(src) {
  if (src) {
    scriptLoadingStatus.delete(src);
  } else {
    scriptLoadingStatus.clear();
  }
}

/**
 * 获取脚本加载状态
 *
 * 用途：
 * - 调试查看脚本加载情况
 * - 在加载前检查脚本状态
 *
 * @param {string} src - 脚本 URL
 * @returns {string|undefined} 返回状态：'loading' | 'loaded' | 'failed' | undefined
 *
 * @example
 * const status = getScriptStatus('https://example.com/script.js');
 * if (status === 'loaded') {
 *   console.log('脚本已加载');
 * }
 */
export function getScriptStatus(src) {
  const status = scriptLoadingStatus.get(src);
  return status?.state;
}
